import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import CommonHeader from '../../util_components/common_header';
import { SelectFilter } from './shared/controls';
import NoPermission from '../../util_components/no_permission';
import * as actions from '../../../redux/action_creators';
import AdditionalPaymentsTab from './additional_payments';
import AuditTrailTab from './audit';
import CloseVersionsTab from './close_versions';
import EmailLogsTab from './email_logs';
import InvoicesTab from './invoices';
import BankPayoutsTab from './payouts';
import {
  can_see_deleted_accounts,
  deleted_banner_text,
  payouts_view_from_search,
  DELETED_VIEW
} from './payouts/deleted_alert';
import { useDeletedSummary } from './payouts/use_deleted_summary';
import RulesEngineTab from './rules_engine';
import { useApprovalsCount } from './rules_engine/use_approvals_count';
import NeedsAttentionTab from './needs_attention';
import OverviewTab from './overview';
import PaymentDetailsTab from './payment_details';
import PaymentEmailsTab from './payment_emails';
import PenaltiesTab from './penalties';
import ReportsTab from './reports';
import CloseSwitchChip from './close_switch/close_switch_chip';
import useCloseSwitch from './close_switch/use_close_switch';
import { fetch_month_summary, fetch_months } from './api';
import { error_message, is_permission_error, month_label } from './format';
import { with_writable_months } from './additional_payments/params';
import { months_view, tab_badges } from './overview/adapters';
import { parse_needs_attention_deep_link } from './needs_attention/params';
import { ist_day } from './shared/ist';
import { audit_link } from './audit/params';
import { BANK_DETAILS_FORBIDDEN_TEXT, tab_allowed_for } from './shared/bank_access';
import { close_versions_badge } from './close_versions/adapters';
import { useCloseVersionsSummary } from './close_versions/use_summary';
import {
  MONTH_TABS,
  NO_MONTH_PICKER_TABS,
  TABS,
  TAB_GROUPS,
  first_tab_of_group,
  group_badges,
  group_of_tab,
  tabs_of_group
} from './tabs';
import { month_from_search, with_month } from './shared/month_query';
import { useMediaQuery } from './use_media_query';
import {
  accept_month_choice,
  parse_overview_deep_link,
  pick_initial_month
} from './overview/params';
import './teacher_payments.css';
import './page_shell.css';

// Teacher Payments module shell (/teacher-payments/:tab).
const PHONE_TABS = '(max-width: 479.98px)';
const system_now = () => new Date();

const TeacherPayments = ({ set_notification_variable, access_type, now = system_now }) => {
  const history = useHistory();
  const location = useLocation();
  const { tab } = useParams();
  // Slack digest deep link, validated once on mount (params.js); later URL edits don't re-apply it.
  const [deep_link, setDeepLink] = useState(() => parse_overview_deep_link(location.search));
  // digest links: ?view=monthly|daily&pay_month=&tier=&day= — whitelisted once, on mount.
  const [na_deep_link] = useState(() =>
    parse_needs_attention_deep_link(location.search, ist_day(now()))
  );
  // Its view/tier/day seed the tab's first mount only; a later visit opens the default view.
  const [na_link_used, setNaLinkUsed] = useState(false);
  const consume_na_deep_link = useCallback(() => setNaLinkUsed(true), []);
  // the needs-review seed is consumed by the first Overview mount, never re-applied.
  const consume_deep_link = useCallback(
    () => setDeepLink((d) => (d.needs_review_only ? { ...d, needs_review_only: false } : d)),
    []
  );
  useEffect(() => {
    if (tab === 'rules') history.replace(`/teacher-payments/rules-engine${location.search}`);
  }, [tab, history, location.search]);

  const active_tab = TABS.some((t) => t.key === tab) ? tab : 'overview';
  // Bank-detail tabs are listed only for the payments roles; a direct link shows a refusal instead.
  const tab_allowed = useMemo(() => tab_allowed_for(access_type), [access_type]);
  const active_tab_allowed = tab_allowed(active_tab);

  const [loading, setLoading] = useState(true);
  const [no_permission, setNoPermission] = useState(false);
  const [months, setMonths] = useState([]);
  // 'loading' | 'ready' | 'empty' (no computed month yet) | 'error' — never an endless spinner.
  const [months_status, setMonthsStatus] = useState('loading');
  const [pay_month, setPayMonth] = useState('');
  const [summary, setSummary] = useState({});
  // Bumped after a close, reopen, issue action or Additional save: the badges and months re-read.
  const [meta_rev, setMetaRev] = useState(0);
  const refresh_meta = useCallback(() => setMetaRev((r) => r + 1), []);
  // The month-close switch chip; a flip bumps switch_rev so Needs attention re-reads close_enabled.
  const {
    data: close_switch,
    reload: reload_close_switch,
    replace: replace_close_switch
  } = useCloseSwitch();
  const [switch_rev, setSwitchRev] = useState(0);

  // Phones: the group's tabs are one labelled select instead of wrapping pill rows.
  const tabs_as_select = useMediaQuery(PHONE_TABS);

  // A refused bank tab shows only its refusal, not the months notices.
  const is_month_tab = MONTH_TABS.indexOf(active_tab) !== -1 && active_tab_allowed;
  const shows_month_picker = NO_MONTH_PICKER_TABS.indexOf(active_tab) === -1;
  const is_additional = active_tab === 'additional-payments';
  // The months read below sees the latest tab, month and clock through refs, not a stale closure.
  const pay_month_ref = useRef(pay_month);
  pay_month_ref.current = pay_month;
  const is_additional_ref = useRef(is_additional);
  is_additional_ref.current = is_additional;
  const now_ref = useRef(now);
  now_ref.current = now;
  // the Additional tab can write the current and previous IST months before /months lists them.
  const tab_months = useMemo(
    () =>
      is_additional && months_status !== 'loading' ? with_writable_months(months, now()) : months,
    [is_additional, months, months_status, now]
  );
  // A month picked on the Additional tab that another tab does not list falls back to that tab's default.
  const shown_month = accept_month_choice(pay_month, tab_months)
    ? pay_month
    : pick_initial_month(tab_months, '');

  // Every read is grant-gated; a 403 means "no grant", which must render the clean
  // no-permission page instead of an error toast on an empty shell.
  const handle_error = useCallback(
    (e, fallback) => {
      if (is_permission_error(e)) {
        setNoPermission(true);
        return;
      }
      set_notification_variable(true, 'error', error_message(e, fallback));
    },
    [set_notification_variable]
  );

  // The URL's ?month= (or a legacy ?pay_month= deep link) is read once, here, through a ref so a later
  // edit of the address bar by the user never re-seeds the month out from under them.
  const url_month_ref = useRef(month_from_search(location.search));

  useEffect(() => {
    let cancelled = false;
    const refresh = meta_rev > 0;
    const asked_on = pay_month_ref.current;
    fetch_months()
      .then((data) => {
        if (cancelled) return;
        const list = months_view(data).months;
        setMonths(list);
        // A refresh keeps the month on screen, including an Additional-only one while that tab is
        // open; only the first load applies the deep link.
        const keepable = is_additional_ref.current
          ? with_writable_months(list, now_ref.current())
          : list;
        // A month picked while this read was in flight is newer than it, so it is never overridden.
        setPayMonth((cur) =>
          cur !== asked_on || accept_month_choice(cur, keepable)
            ? cur
            : pick_initial_month(
                list,
                cur || url_month_ref.current || deep_link.pay_month || na_deep_link.pay_month
              )
        );
        setMonthsStatus(list.length ? 'ready' : 'empty');
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        handle_error(e, 'Failed to load teacher payment months.');
        // A failed refresh leaves the page as it was; only a failed first load has nothing to show.
        if (!refresh) setMonthsStatus('error');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [handle_error, deep_link, na_deep_link, meta_rev]);

  useEffect(() => {
    if (!shown_month) {
      setSummary({}); // no month → no badges from a previous month
      return undefined;
    }
    let cancelled = false;
    fetch_month_summary(shown_month)
      .then((data) => {
        if (!cancelled) setSummary(tab_badges(data));
      })
      .catch((e) => {
        // Badge counts are decoration — never block the page on them.
        if (!cancelled) handle_error(e, 'Failed to load month summary counts.');
      });
    return () => {
      cancelled = true;
    };
  }, [shown_month, handle_error, meta_rev]);

  // one summary read per month (and per meta_rev) badges the tab and feeds it; a failed read only
  // hides the badge, the tab says so itself.
  const close_versions_summary = useCloseVersionsSummary(shown_month, meta_rev);
  // rule changes waiting for approval badge the Rules tab; a write there re-reads it. While the tab
  // is open its own queue poll feeds the badge, so the endpoint is polled once.
  const [approvals_rev, setApprovalsRev] = useState(0);
  const approvals_changed = useCallback(() => setApprovalsRev((r) => r + 1), []);
  const [approvals_waiting, setApprovalsWaiting] = useApprovalsCount(approvals_rev, {
    paused: active_tab === 'rules-engine'
  });
  // deleted accounts with money due - a red banner on every tab and the Bank payouts badge.
  const [payouts_rev, setPayoutsRev] = useState(0);
  const deleted_summary = useDeletedSummary(
    meta_rev + payouts_rev,
    can_see_deleted_accounts(access_type)
  );
  const deleted_banner = deleted_banner_text(deleted_summary);
  // Which Bank payouts section to open: ?view= on the first load, then every banner click (rev re-applies it).
  const [payouts_view_req, setPayoutsViewReq] = useState(() => ({
    view: payouts_view_from_search(location.search),
    rev: 0
  }));

  // The URL mirrors the shown month (replace, not push: a month change is not a history step) on
  // every tab that has a month picker.
  useEffect(() => {
    if (!shows_month_picker) return;
    // A Back/Forward to another listed month is adopted by the effect below, not overwritten here.
    const url_month = month_from_search(location.search);
    if (
      history.action === 'POP' &&
      url_month &&
      url_month !== shown_month &&
      accept_month_choice(url_month, tab_months)
    )
      return;
    const next_qs = with_month(location.search, shown_month);
    if (location.search.replace(/^\?/, '') !== next_qs) {
      history.replace({ pathname: location.pathname, search: next_qs ? `?${next_qs}` : '' });
    }
  }, [shows_month_picker, shown_month, history, location.pathname, location.search, tab_months]);

  // Back/Forward (POP) lands on an entry whose ?month= may differ from the month in state; adopt it, else
  // the mirror effect above would rewrite the URL back and undo the navigation.
  useEffect(() => {
    if (history.action !== 'POP') return;
    const m = month_from_search(location.search);
    if (m && m !== pay_month_ref.current && accept_month_choice(m, tab_months)) setPayMonth(m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Tab switches keep the month (a bare history.push would otherwise drop every query param).
  const go_tab = useCallback(
    (key) =>
      history.push({
        pathname: `/teacher-payments/${key}`,
        search: shown_month ? `?${with_month('', shown_month)}` : ''
      }),
    [history, shown_month]
  );

  const open_deleted_accounts = useCallback(() => {
    setPayoutsViewReq((r) => ({ view: DELETED_VIEW, rev: r.rev + 1 }));
    history.push({
      pathname: '/teacher-payments/bank-payouts',
      search: `?${with_month(`view=${DELETED_VIEW}`, shown_month)}`
    });
  }, [history, shown_month]);

  const notify = useCallback(
    (type, message) => set_notification_variable(true, type, message),
    [set_notification_variable]
  );

  const on_switch_changed = useCallback(
    (data) => {
      replace_close_switch(data);
      setSwitchRev((r) => r + 1);
      if (data && typeof data.close_enabled === 'boolean') {
        notify('success', `Month close is now ${data.close_enabled ? 'ON' : 'OFF'}.`);
      }
    },
    [replace_close_switch, notify]
  );

  // only a month this tab lists is accepted (the empty "Select a month" option never is).
  const choose_month = (value) => {
    if (accept_month_choice(value, tab_months)) setPayMonth(value);
  };

  const selected_month = useMemo(
    () => tab_months.find((m) => m.pay_month === shown_month) || {},
    [tab_months, shown_month]
  );

  if (no_permission) {
    return <NoPermission />;
  }

  const month_options = tab_months.map((m) => ({ value: m.pay_month, label: m.label }));

  const active_group = group_of_tab(active_tab);
  const group_tabs = tabs_of_group(active_group, tab_allowed);
  // A hidden tab (Audit trail) is routable but not listed: the select then shows a placeholder.
  const tab_listed = group_tabs.some((t) => t.key === active_tab);
  // One count per tab; each group button shows the sum of its listed tabs' counts.
  const tab_counts = TABS.reduce((acc, t) => {
    let badge = summary[t.key] || 0;
    if (t.key === 'close-versions') badge = close_versions_badge(close_versions_summary.data) || 0;
    else if (t.key === 'rules-engine') badge = approvals_waiting;
    else if (t.key === 'bank-payouts') badge = deleted_summary.count;
    acc[t.key] = badge;
    return acc;
  }, {});
  const group_counts = group_badges(tab_counts, tab_allowed);

  return (
    <CommonHeader loading={loading} title="Teacher Payments" class_name="tp-fullwidth">
      <div className="tp-page">
        <div className="tp-header">
          <span className="tp-title">Teacher Payments</span>
          {shows_month_picker && selected_month.badge && selected_month.badge.key ? (
            <span
              className={`tp-lifecycle tp-lifecycle-${selected_month.badge.key}`}
              title="Lifecycle of this month across its teachers; close and reopen them under Needs attention"
            >
              {selected_month.badge.label}
            </span>
          ) : null}
          <CloseSwitchChip
            data={close_switch}
            on_changed={on_switch_changed}
            on_stale={reload_close_switch}
          />
          {shows_month_picker ? (
            <div className="tp-hd-month">
              <SelectFilter
                id="tp-month-select"
                label="Month"
                aria_label="Payment month"
                value={shown_month}
                options={month_options}
                all_label={shown_month ? undefined : 'Select a month'}
                active={false}
                onChange={choose_month}
              />
            </div>
          ) : null}
        </div>

        {deleted_banner ? (
          <div className="tp-banner tp-banner-error tp-deleted-banner" role="alert">
            {deleted_banner} –{' '}
            <button type="button" className="tp-link" onClick={open_deleted_accounts}>
              Review
            </button>
          </div>
        ) : null}

        <div className="tp-groups" aria-label="Tab groups">
          {TAB_GROUPS.map((g) => (
            <button
              key={g.key}
              type="button"
              className={`tp-group ${active_group === g.key ? 'on' : ''}`}
              aria-pressed={active_group === g.key}
              onClick={() => {
                if (active_group !== g.key) go_tab(first_tab_of_group(g.key, tab_allowed));
              }}
            >
              {g.label}
              {group_counts[g.key] ? (
                <span className="tp-tab-ct">{group_counts[g.key]}</span>
              ) : null}
            </button>
          ))}
        </div>
        {tabs_as_select ? (
          <div className="tp-tab-pick">
            <SelectFilter
              id="tp-section-select"
              label="Section"
              value={tab_listed ? active_tab : ''}
              options={group_tabs.map((t) => ({
                value: t.key,
                label: tab_counts[t.key] ? `${t.label} (${tab_counts[t.key]})` : t.label
              }))}
              all_label={tab_listed ? undefined : 'Choose a section'}
              active={false}
              onChange={go_tab}
            />
          </div>
        ) : (
          <div className="tp-tabs">
            {group_tabs.map((t) => {
              const badge = tab_counts[t.key] || 0;
              return (
                <button
                  key={t.key}
                  type="button"
                  className={`tp-tab ${active_tab === t.key ? 'on' : ''}`}
                  aria-current={active_tab === t.key ? 'page' : undefined}
                  onClick={() => go_tab(t.key)}
                >
                  {t.label}
                  {badge ? <span className="tp-tab-ct">{badge}</span> : null}
                </button>
              );
            })}
          </div>
        )}

        {!active_tab_allowed ? (
          <div className="tp-banner tp-banner-info" role="status">
            {BANK_DETAILS_FORBIDDEN_TEXT}
          </div>
        ) : null}
        {is_month_tab && !is_additional && months_status === 'empty' ? (
          <div className="tp-empty">
            No payment months yet. The module fills in once the first month has been computed.
          </div>
        ) : null}
        {is_month_tab && months_status === 'error' ? (
          <div className="tp-banner tp-banner-error">
            Could not load the payment months. Reload the page to try again.
          </div>
        ) : null}
        {active_tab === 'overview' && months_status === 'ready' && shown_month ? (
          <OverviewTab
            pay_month={shown_month}
            month_label={selected_month.label || month_label(shown_month)}
            initial_needs_review={deep_link.needs_review_only}
            on_deep_link_consumed={consume_deep_link}
            on_open_needs_attention={() => go_tab('needs-attention')}
            on_open_penalties={() => go_tab('penalties')}
            on_error={handle_error}
            on_notify={notify}
          />
        ) : null}
        {active_tab === 'needs-attention' && months_status === 'ready' && shown_month ? (
          <NeedsAttentionTab
            pay_month={shown_month}
            month_label={selected_month.label || month_label(shown_month)}
            deep_link={na_link_used ? {} : na_deep_link}
            on_deep_link_consumed={consume_na_deep_link}
            months={months}
            on_month={choose_month}
            on_changed={refresh_meta}
            on_error={handle_error}
            now={now}
            switch_rev={switch_rev}
            on_summary_loaded={reload_close_switch}
          />
        ) : null}
        {active_tab === 'penalties' && months_status === 'ready' && shown_month ? (
          <PenaltiesTab
            key={shown_month}
            pay_month={shown_month}
            month_label={selected_month.label || month_label(shown_month)}
            month_badge={selected_month.badge}
            on_error={handle_error}
            on_changed={refresh_meta}
          />
        ) : null}
        {is_additional && months_status !== 'loading' && shown_month ? (
          <AdditionalPaymentsTab
            pay_month={shown_month}
            month_label={selected_month.label || month_label(shown_month)}
            month_badge={selected_month.badge}
            now={now}
            on_error={handle_error}
            on_notify={notify}
            on_month_change={choose_month}
            on_changed={refresh_meta}
          />
        ) : null}
        {active_tab === 'reports' && months_status === 'ready' && shown_month ? (
          <ReportsTab
            pay_month={shown_month}
            month_label={selected_month.label || month_label(shown_month)}
            on_error={handle_error}
            on_open_audit={() => history.push(audit_link(shown_month, ''))}
          />
        ) : null}
        {active_tab === 'close-versions' && months_status === 'ready' && shown_month ? (
          <CloseVersionsTab
            key={shown_month}
            pay_month={shown_month}
            month_label={selected_month.label || month_label(shown_month)}
            summary={close_versions_summary}
            on_error={handle_error}
            on_changed={refresh_meta}
            on_open_needs_attention={() => go_tab('needs-attention')}
          />
        ) : null}
        {active_tab === 'email-logs' && months_status === 'ready' && shown_month ? (
          <EmailLogsTab
            key={shown_month}
            pay_month={shown_month}
            month_label={selected_month.label || month_label(shown_month)}
            on_error={handle_error}
            on_open_payment_emails={() => go_tab('payment-emails')}
          />
        ) : null}
        {active_tab === 'invoices' && months_status === 'ready' && shown_month ? (
          <InvoicesTab
            key={shown_month}
            pay_month={shown_month}
            month_label={selected_month.label || month_label(shown_month)}
            on_error={handle_error}
            on_notify={notify}
          />
        ) : null}
        {active_tab === 'bank-payouts' &&
        active_tab_allowed &&
        months_status === 'ready' &&
        shown_month ? (
          <BankPayoutsTab
            pay_month={shown_month}
            month_label={selected_month.label || month_label(shown_month)}
            months={tab_months}
            now={now}
            on_error={handle_error}
            on_notify={notify}
            view_request={payouts_view_req}
            deleted_count={deleted_summary.count}
            on_changed={() => setPayoutsRev((r) => r + 1)}
          />
        ) : null}
        {/* The audit trail also opens on a teacher-only link, so it waits only for the months read. */}
        {active_tab === 'audit' && months_status !== 'loading' ? (
          <AuditTrailTab pay_month={shown_month} months={months} on_error={handle_error} />
        ) : null}
        {active_tab === 'rules-engine' ? (
          <RulesEngineTab
            on_error={handle_error}
            now={now}
            on_approvals_changed={approvals_changed}
            on_approvals_count={setApprovalsWaiting}
          />
        ) : null}
        {active_tab === 'payment-details' && active_tab_allowed ? (
          <PaymentDetailsTab on_error={handle_error} on_notify={notify} />
        ) : null}
        {active_tab === 'payment-emails' ? (
          <PaymentEmailsTab on_error={handle_error} on_notify={notify} />
        ) : null}
      </div>
    </CommonHeader>
  );
};

const mapDispatchToProps = (dispatch) => ({
  set_notification_variable: (show, type, mes) => {
    dispatch(actions.set_notification_variable(show, type, mes));
  }
});

const mapStateToProps = (state) => ({
  access_type: state && state.home ? state.home.access_type : ''
});

export default connect(mapStateToProps, mapDispatchToProps)(TeacherPayments);
